import { Inbox, ClipboardList, LineChart, Brain } from "lucide-react";

const PILLARS = [
  {
    tag: "01 / Inbound",
    icon: Inbox,
    title: "Lead & Follow-Up Review",
    body: "We trace every way a lead reaches you — call, form, DM, referral — and map how fast (or slow) each one actually gets a human response. We find where leads silently disappear.",
    checks: ["Response-time audit", "After-hours coverage gaps", "Drop-off & ghosted-lead map"],
  },
  {
    tag: "02 / Operations",
    icon: ClipboardList,
    title: "Operations & Admin Review",
    body: "We look at the repeat tasks eating your week: quoting, scheduling, data entry, invoice chasing, reporting. Anything that's manual, repetitive and rules-based is a candidate for automation.",
    checks: ["Manual task inventory", "Tool-to-tool handoff gaps", "Reporting & visibility audit"],
  },
  {
    tag: "03 / Sales",
    icon: LineChart,
    title: "Sales Process Review",
    body: "We walk your funnel end-to-end — first touch to signed customer — and flag where the pipeline leaks: slow quotes, forgotten follow-ups, weak nurture, inconsistent messaging.",
    checks: ["Pipeline leak points", "Follow-up cadence review", "Conversion friction map"],
  },
  {
    tag: "04 / Roadmap",
    icon: Brain,
    title: "AI Opportunity Roadmap",
    body: "Not every bottleneck needs AI. We tell you which ones genuinely do, which are better fixed with simple automation, and which you should leave alone. Ranked by effort vs. impact.",
    checks: ["Top 3 ranked fixes", "Tools / workflow recommendations", "What to ignore for now"],
  },
];

export default function AssessmentPillars() {
  return (
    <section
      id="assessment"
      className="bg-[#FAFAFA] border-b border-zinc-200"
      data-testid="assessment-section"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
        <div className="max-w-3xl reveal">
          <div className="flex items-center gap-3">
            <span className="inline-block h-px w-10 bg-orange-600" />
            <span className="font-mono text-xs uppercase tracking-[0.22em] text-orange-600 font-semibold">
              What We Assess
            </span>
          </div>
          <h2
            className="mt-6 font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-zinc-950"
            data-testid="assessment-heading"
          >
            Four lenses. One honest picture of your operation.
          </h2>
          <p className="mt-6 text-lg text-zinc-600 leading-relaxed">
            We don't throw AI at everything. We look at your business through four specific
            lenses, identify the bottlenecks, and rank them by real business impact.
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <article
                key={p.title}
                className="group relative bg-white p-8 sm:p-10 transition-all duration-300 hover:bg-zinc-50 reveal"
                data-testid={`pillar-${i}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500">
                    {p.tag}
                  </span>
                  <span className="inline-flex items-center justify-center w-10 h-10 border border-zinc-300 group-hover:border-zinc-950 group-hover:bg-zinc-950 transition-colors">
                    <Icon
                      size={18}
                      className="text-zinc-700 group-hover:text-white transition-colors"
                    />
                  </span>
                </div>

                <h3 className="mt-6 font-display font-semibold text-2xl sm:text-[28px] tracking-tight text-zinc-950">
                  {p.title}
                </h3>
                <p className="mt-3 text-zinc-600 leading-relaxed">{p.body}</p>

                <ul className="mt-6 space-y-2">
                  {p.checks.map((c) => (
                    <li key={c} className="flex items-start gap-3 text-sm text-zinc-700">
                      <span className="font-mono text-orange-600 mt-0.5">›</span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
